import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSite } from '../useSite';
import { Container, Overline } from './Primitives';
import { TestimonialForm } from './TestimonialForm';

const API = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ rating }) => (
  <div className="flex gap-0.5" aria-label={`${rating}/5`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <span key={s} className={s <= rating ? 'text-moss-green' : 'text-tan/60'} aria-hidden="true">★</span>
    ))}
  </div>
);

const Card = ({ item, index, lang }) => (
  <figure
    data-testid={`testimonial-card-${index}`}
    className="flex w-[85%] shrink-0 snap-center flex-col border border-tan/50 bg-white p-7 sm:w-[60%] md:w-auto md:shrink"
  >
    <Stars rating={item.rating || 5} />
    <blockquote className="mt-5 line-clamp-6 flex-1 font-serif text-xl leading-snug text-cafe-noir">“{item.comment}”</blockquote>
    <figcaption className="mt-6 border-t border-tan/40 pt-4 text-sm">
      <span className="font-semibold text-kombu-green">{item.name}</span>
      <span className="block text-xs text-cafe-noir/60">
        {new Date(item.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'short' })}
      </span>
    </figcaption>
  </figure>
);

export const Testimonials = () => {
  const { t, lang } = useSite();
  const tt = t.testimonials;
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/testimonials`, { timeout: 15000 })
      .then(({ data }) => Array.isArray(data) && setItems(data))
      .catch(() => {});
  }, []);

  const avg = items.length ? (items.reduce((s, r) => s + (r.rating || 5), 0) / items.length).toFixed(1) : null;

  return (
    <section id={lang === 'es' ? 'comentarios' : 'testimonials'} className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="testimonials-section">
      <Container>
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Overline>{tt.overline}</Overline>
            <h2 className="mt-4 font-serif text-3xl font-medium text-kombu-green md:text-[2.75rem]">{tt.title}</h2>
          </div>
          {avg && (
            <p className="text-sm text-cafe-noir/80" data-testid="testimonials-summary">
              <span className="font-semibold text-kombu-green">★ {avg}</span> / 5 · {tt.count(items.length)}
            </p>
          )}
        </div>
        {items.length > 0 ? (
          <>
            <div
              data-testid="testimonials-carousel"
              className="scrollbar-hide -mx-5 mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 lg:grid-cols-3"
              style={{ scrollbarWidth: 'none' }}
            >
              {items.slice(0, 6).map((it, i) => <Card key={it.id} item={it} index={i} lang={lang} />)}
            </div>
            {items.length > 1 && <p className="mt-2 text-center text-xs text-cafe-noir/60 md:hidden">← {tt.swipe} →</p>}
          </>
        ) : (
          <div className="mt-12 border-t border-tan/60 pt-10" data-testid="testimonials-empty">
            <div className="border-l-2 border-moss-green pl-6 md:pl-8">
              <p className="font-serif text-2xl text-kombu-green md:text-3xl">{tt.emptyTitle}</p>
              <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-cafe-noir/80">{tt.empty}</p>
              <p className="mt-3 font-serif text-lg italic text-moss-green">{tt.emptyCta}</p>
            </div>
          </div>
        )}
        <TestimonialForm />
      </Container>
    </section>
  );
};
